import { memo } from "react";
import { ActivityIndicator } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { PressableScale } from "pressto";
import { Button, Card, Text, View } from "@src/components";
import { Icon } from "@src/components/Icon";
import { AlarmStateBadge } from "./AlarmStateBadge";
import { DiagonalStripeSquare } from "@src/features/schedules/components/DiagonalStripeSquare";
import { formatExceptionDate, formatExceptionTime } from "@src/features/alarms/utils/exception-format";
import type { FC } from "react";
import type { AlarmAction, AlarmException } from "@src/services/api/types";

export type AggregatedExceptionGroup = {
  key: string;
  reason: string;
  action: AlarmAction;
  period: AlarmException["period"];
  cameraKeys: string[];
  totalCameras: number;
  timezone: string;
};

interface ManageExceptionsContentProps {
  isLoading: boolean;
  exceptions: AggregatedExceptionGroup[];
  onEditException: (group: AggregatedExceptionGroup) => void;
  onCreateException: () => void;
}

export const ManageExceptionsContent: FC<ManageExceptionsContentProps> = memo(
  ({ isLoading, exceptions, onEditException, onCreateException }) => {
    const { theme } = useUnistyles();

    if (isLoading) {
      return (
        <View flex={1} justifyContent="center" alignItems="center">
          <ActivityIndicator color={theme.colors.text} />
        </View>
      );
    }

    return (
      <View gap="3xl" flex={1}>
        {exceptions.length === 0 && (
          <Card py={16}>
            <View alignItems="center">
              <Icon
                name="calendarOutline"
                size={48}
                color={theme.colors.textMuted}
                style={{ marginBottom: theme.spacing["2xl"] }}
              />
              <Text textStyle="md/semibold" fontWeight="600" mb="xs" textAlign="center">
                No upcoming exceptions
              </Text>
              <Text color="textMuted" textStyle="sm/regular" textAlign="center" px="xl">
                Exceptions allow you to make temporary changes to your routine, such as arming the site all day on key
                dates.
              </Text>
            </View>
          </Card>
        )}

        {exceptions.map((group) => (
          <ExceptionCard key={group.key} group={group} onPress={() => onEditException(group)} />
        ))}

        <View style={{ maxWidth: 400, alignSelf: "center", width: "100%" }}>
          <Button
            label="Create new"
            variant="primary"
            variantColor="blue"
            size="lg"
            onPress={onCreateException}
            iconRight={<Icon name="plus" color={theme.colors.white.default} size={20} />}
          />
        </View>
      </View>
    );
  },
);

ManageExceptionsContent.displayName = "ManageExceptionsContent";

const ExceptionCard: FC<{ group: AggregatedExceptionGroup; onPress: () => void }> = memo(({ group, onPress }) => {
  const { theme } = useUnistyles();
  const count = group.cameraKeys.length;
  const cameraLabel =
    count === group.totalCameras ? "All cameras"
    : count === 1 ? "1 camera"
    : `${count} cameras`;

  return (
    <PressableScale onPress={onPress}>
      <Card>
        <View flexDirection="row" justifyContent="space-between" gap="md">
          <View flex={1}>
            <View flexDirection="row" alignItems="center" gap="md" mb="2xl">
              <View style={{ overflow: "hidden" }}>
                <DiagonalStripeSquare state={group.action} size={20} />
              </View>
              <Text textStyle="sm/medium" numberOfLines={2} ellipsizeMode="tail" style={{ flexShrink: 1 }}>
                {group.reason}
              </Text>
              <AlarmStateBadge state={group.action} />
            </View>
            <View flexDirection="row" alignItems="center" gap="xs" mb="xs">
              <Icon name="videoOutline" color={theme.colors.textMuted} size={16} />
              <Text textStyle="xs/regular" color="textMuted">
                {cameraLabel}
              </Text>
            </View>
            <View flexDirection="row" alignItems="center" gap="xs">
              <Icon name="calendarOutline" color={theme.colors.textMuted} size={16} />
              <Text textStyle="xs/regular" color="textMuted">
                {formatExceptionDate(group.period, group.timezone)}
              </Text>
              <Text textStyle="xs/regular" color="textMuted">
                •
              </Text>
              <Icon name="clockOutline" color={theme.colors.textMuted} size={16} />
              <Text textStyle="xs/regular" color="textMuted">
                {formatExceptionTime(group.period, group.timezone)}
              </Text>
            </View>
          </View>
          <Icon name="chevronRight" color={theme.colors.textMuted} size={24} />
        </View>
      </Card>
    </PressableScale>
  );
});

ExceptionCard.displayName = "ExceptionCard";
